import { type OnboardingMilestone, useReferralOnboardingProgressQuery } from '@cartwheel/api/v1'
import { ErrorView, LoadingView, StepperVertical } from '@cartwheel/components'

interface Props {
  referralId: string
}

function humanizeMilestone(name: OnboardingMilestone['milestone']): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatCompletedAt(completedAt: string | null | undefined): string | null {
  if (!completedAt) return null
  return new Date(completedAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function MilestoneStepLabel({ milestone }: { milestone: OnboardingMilestone }) {
  const completedAt = formatCompletedAt(milestone.completed_at)

  return (
    <div className="flex flex-col">
      <span className="font-semibold text-charcoal-75">
        {humanizeMilestone(milestone.milestone)}
      </span>
      <span className="text-sm text-charcoal-50">
        <span className="capitalize">{milestone.status}</span>
        {completedAt ? ` · ${completedAt}` : ''}
        {milestone.reason_code ? ` · ${milestone.reason_code}` : ''}
      </span>
    </div>
  )
}

export default function OnboardingProgressPanel({ referralId }: Props) {
  const { data, isPending, error } = useReferralOnboardingProgressQuery({
    path: { referral_id: referralId },
  })

  if (isPending) return <LoadingView title="Loading onboarding progress…" />
  if (error) return <ErrorView title="Couldn't load onboarding progress" />
  if (!data) return null

  const milestones = data.milestones ?? []

  return (
    <section className="my-10">
      <h2 className="text-xl font-semibold text-charcoal-75">Onboarding Progress</h2>
      <p className="mb-6 text-sm text-charcoal-50">
        Step {data.current_step_number} of {data.total_steps}
      </p>

      {milestones.length === 0 ? (
        <p className="text-charcoal-50">No onboarding milestones recorded yet.</p>
      ) : (
        <StepperVertical
          steps={milestones.map((milestone) => ({
            complete: milestone.status !== 'pending',
            label: <MilestoneStepLabel milestone={milestone} />,
          }))}
        />
      )}
    </section>
  )
}
